/**
 * =============================================================================
 * CommissionsTableView — Tabela reutilizável de comissões (módulo Indicação)
 *
 * Padronizado 1:1 com a página "Todos Negócios" (`CrmDeals`):
 *  - Tabela full-bleed (sem moldura `rounded-lg border` externa).
 *  - SEM sub-barra interna (resumo + pills) — o filtro de status agora vive
 *    na barra de filtros principal da página, como `<Select>` inline.
 *  - Mantém: barra lateral colorida por status no início da linha, headers
 *    ordenáveis com chevrons, badges compactos, skeleton e estado vazio.
 *  - Server-side aware: paginação/filtros controlados pela página via RPC.
 *  - Ordenação local (página atual), espelhando `ChargesTableView`.
 *
 * @version 2.0.0
 * RELEASE:
 *   2026-04-25 v1.0.0 - Padronização visual com Cobranças/Financeiro.
 *   2026-04-25 v2.0.0 - Refator para paridade com CrmDeals: removida sub-barra
 *                       interna (resumo + pills), removidas bordas externas,
 *                       removidas props `statusFilter`/`onStatusFilterChange`.
 * =============================================================================
 */
import React, { useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  HandCoins,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  MoreHorizontal,
  CheckCircle2,
  CircleDollarSign,
  XCircle,
  ChevronRight,
  ExternalLink,
  Eye,
} from 'lucide-react';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Badge } from '@/components/ui/badge';
import {
  CommissionStatusBadge,
  COMMISSION_STATUS_CONFIG,
} from './CommissionStatusBadge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { StatusBar } from '@/components/referral/shared/StatusBar';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import type {
  PaginatedCommission,
  CommissionStatus,
} from '@/hooks/useReferralCommissionsPaginated';

// ============================================================================
// CONSTANTES E MAPEAMENTOS
// ============================================================================

/** Filtro por status (inclui "all"). */
export type CommissionStatusFilter = 'all' | CommissionStatus;

/**
 * Ações disponíveis no menu de cada linha.
 * - `view`     → abre o `CommissionDetailSheet` (Ver detalhes).
 * - `approve`  → aprova a comissão (status pending → approved).
 * - `mark_paid`→ gera/reabre o payout e redireciona p/ Pagamentos.
 * - `cancel`   → cancela a comissão.
 */
export type CommissionRowAction = 'view' | 'approve' | 'mark_paid' | 'cancel';

// Mapa de status migrado para `CommissionStatusBadge` (fonte única de verdade
// para label/cor de status de comissão). Importado abaixo via
// `COMMISSION_STATUS_CONFIG` para uso na barra lateral colorida da linha.

/** Mapeamento de tipo de recompensa → label curto exibido em badge. */
const REWARD_TYPE_LABEL: Record<string, string> = {
  fixed: 'Valor',
  percentage: '% Receita',
  points: 'Pontos',
  recurring: 'Recorrente',
  tiered: 'Em níveis',
};

// (Removido em v2.0.0): a lista FILTER_STATUSES de pills foi descontinuada.
// O filtro de status agora vive na barra de filtros principal da página
// (`ReferralCommissions.tsx`) como `<Select>` inline, espelhando o padrão
// adotado pela página "Todos Negócios" (`CrmDeals`).

/** Campos suportados pela ordenação local (página atual). */
type SortField =
  | 'affiliate'
  | 'reward_value'
  | 'status'
  | 'created_at'
  | 'approved_at'
  | 'paid_at';

type SortDirection = 'asc' | 'desc';

// ============================================================================
// UTILITÁRIOS
// ============================================================================

/**
 * Cor da barra lateral por status — usa o mesmo mapa visual do
 * `CommissionStatusBadge`, mantendo perfeita consistência.
 */
function getCommissionStatusBarColor(status: CommissionStatus): string {
  return COMMISSION_STATUS_CONFIG[status]?.bar ?? 'bg-muted-foreground/40';
}

/** Formata valor monetário em BRL (moeda fixa do módulo). */
function formatBRL(value: number | null | undefined): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value ?? 0));
}

// ============================================================================
// COMPONENTE: HEADER ORDENÁVEL (cópia 1:1 do ChargesTableView)
// ============================================================================

function SortableHeader({
  field,
  label,
  currentField,
  direction,
  onSort,
  align = 'left',
}: {
  field: SortField;
  label: string;
  currentField: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
  align?: 'left' | 'right' | 'center';
}) {
  const isActive = currentField === field;
  return (
    <TableHead
      className={cn(
        'text-xs font-medium py-2 cursor-pointer hover:bg-muted/30 transition-colors select-none',
        align === 'right' && 'text-right pr-4',
        align === 'center' && 'text-center',
      )}
      onClick={() => onSort(field)}
    >
      <div
        className={cn(
          'flex items-center gap-1',
          align === 'right' && 'justify-end',
          align === 'center' && 'justify-center',
        )}
      >
        <span>{label}</span>
        {isActive ? (
          direction === 'asc' ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </div>
    </TableHead>
  );
}

// ============================================================================
// COMPONENTE: LINHA DA TABELA
// ============================================================================

function CommissionTableRow({
  commission,
  onAction,
  onOpenDetails,
  onOpenPayouts,
  onOpenDeal,
  onOpenReferral,
  onOpenCustomer,
}: {
  commission: PaginatedCommission;
  onAction: (commission: PaginatedCommission, action: CommissionRowAction) => void;
  onOpenDetails?: (commission: PaginatedCommission) => void;
  onOpenPayouts?: (commission: PaginatedCommission) => void;
  onOpenDeal?: (dealId: string) => void;
  onOpenReferral?: (referralId: string) => void;
  onOpenCustomer?: (customerId: string) => void;
}) {
  // Status visual agora é renderizado pelo `CommissionStatusBadge` (dot + texto).

  // Valor exibido — pontos ou moeda
  const valorPrincipal =
    commission.reward_type === 'points'
      ? `${commission.points_earned ?? 0} pts`
      : formatBRL(commission.reward_value);

  // Helpers de "clicabilidade" — mantêm o pill estilizado mesmo sem callback
  const dealClickable = !!onOpenDeal && !!commission.deal_id;
  const referralClickable = !!onOpenReferral && !!commission.referral_id;
  const customerClickable = !!onOpenCustomer && !!commission.customer_id;
  const payoutsCount = commission.payouts_count ?? 0;
  const payoutsClickable = !!onOpenPayouts && payoutsCount > 0;
  // Coluna "Ref." clicável quando há handler de detalhes.
  const refClickable = !!onOpenDetails;

  return (
    <TableRow className="hover:bg-muted/40 border-b border-border/40 group transition-colors">
      {/* 1. Barra lateral colorida por status */}
      <TableCell className="p-0 w-2">
        <StatusBar colorClass={getCommissionStatusBarColor(commission.status)} />
      </TableCell>

      {/*
        2. Ref. — 8 primeiros dígitos do UUID.
        Quando há `onOpenDetails` disponível, vira affordance clicável que
        abre o `CommissionDetailSheet`. Mantém o estilo monoespaçado.
      */}
      <TableCell className="px-2 py-1.5">
        {refClickable ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetails!(commission);
            }}
            title="Ver detalhes da comissão"
            className="inline-flex items-center gap-1 font-mono text-[11px] text-primary hover:underline cursor-pointer"
          >
            {commission.id.substring(0, 8).toUpperCase()}
            <ExternalLink className="h-2.5 w-2.5 opacity-60" />
          </button>
        ) : (
          <span className="font-mono text-[11px] text-muted-foreground">
            {commission.id.substring(0, 8).toUpperCase()}
          </span>
        )}
      </TableCell>

      {/* 3. N. Negócio — UUID 8 chars, clicável quando deal_id presente */}
      <TableCell className="px-2 py-1.5">
        {commission.deal_id ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (dealClickable) onOpenDeal!(commission.deal_id!);
            }}
            disabled={!dealClickable}
            className={cn(
              'inline-flex items-center gap-1 font-mono text-[11px]',
              dealClickable
                ? 'text-primary hover:underline cursor-pointer'
                : 'text-muted-foreground cursor-default',
            )}
            title={dealClickable ? 'Abrir negócio' : undefined}
          >
            {commission.deal_id.substring(0, 8).toUpperCase()}
            {dealClickable && (
              <ExternalLink className="h-2.5 w-2.5 opacity-60" />
            )}
          </button>
        ) : (
          <span className="font-mono text-[11px] text-muted-foreground">—</span>
        )}
      </TableCell>

      {/* 4. Afiliado (nome + e-mail) — movido para depois de N. Negócio */}
      <TableCell className="px-2 py-1.5 max-w-[170px]">
        <div className="font-medium text-sm truncate">
          {commission.affiliate_full_name ?? '—'}
        </div>
        <div className="text-[11px] text-muted-foreground truncate">
          {commission.affiliate_email ?? ''}
        </div>
      </TableCell>

      {/* 5. N. Indicação — UUID 8 chars, clicável quando referral_id presente */}
      <TableCell className="px-2 py-1.5">
        {commission.referral_id ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (referralClickable) onOpenReferral!(commission.referral_id!);
            }}
            disabled={!referralClickable}
            className={cn(
              'inline-flex items-center gap-1 font-mono text-[11px]',
              referralClickable
                ? 'text-primary hover:underline cursor-pointer'
                : 'text-muted-foreground cursor-default',
            )}
            title={referralClickable ? 'Abrir indicação' : undefined}
          >
            {commission.referral_id.substring(0, 8).toUpperCase()}
            {referralClickable && (
              <ExternalLink className="h-2.5 w-2.5 opacity-60" />
            )}
          </button>
        ) : (
          <span className="font-mono text-[11px] text-muted-foreground">—</span>
        )}
      </TableCell>

      {/* 6. Cliente — clicável quando customer_id presente; senão mostra lead */}
      <TableCell className="px-2 py-1.5 max-w-[170px]">
        {commission.customer_id ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (customerClickable) onOpenCustomer!(commission.customer_id!);
            }}
            disabled={!customerClickable}
            className={cn(
              'inline-flex items-center gap-1 min-w-0 max-w-full text-left',
              customerClickable ? 'cursor-pointer' : 'cursor-default',
            )}
            title={customerClickable ? 'Abrir cliente' : undefined}
          >
            <span
              className={cn(
                'text-sm font-medium truncate',
                customerClickable
                  ? 'text-primary hover:underline'
                  : 'text-foreground',
              )}
            >
              {commission.customer_name ?? '—'}
            </span>
            {customerClickable && (
              <ExternalLink className="h-2.5 w-2.5 opacity-60 shrink-0" />
            )}
          </button>
        ) : (
          <span className="text-xs text-muted-foreground truncate block">
            {commission.referral_lead_name
              ? `${commission.referral_lead_name} (lead)`
              : '—'}
          </span>
        )}
      </TableCell>

      {/* 7. Programa */}
      <TableCell className="px-2 py-1.5 text-xs text-muted-foreground max-w-[130px]">
        <span className="truncate block">
          {commission.program_name ?? '—'}
        </span>
      </TableCell>

      {/* 8. Campanha */}
      <TableCell className="px-2 py-1.5 text-xs text-muted-foreground max-w-[130px]">
        <span className="truncate block">{commission.campaign_name ?? '—'}</span>
      </TableCell>

      {/* 9. Pagamentos — pill em UMA LINHA SÓ (sem quebra), formato compacto */}
      <TableCell className="px-2 py-1.5">
        {payoutsClickable ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenPayouts!(commission);
            }}
            title="Ver pagamentos vinculados a esta comissão"
            className={cn(
              'inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-border/60 bg-muted/30',
              'px-2 py-0.5 text-[11px] font-medium tabular-nums text-foreground',
              'hover:border-primary/60 hover:bg-primary/10 hover:text-primary transition-colors cursor-pointer',
            )}
          >
            {/* Formato compacto evita quebrar a linha do pill */}
            {payoutsCount} {payoutsCount === 1 ? 'pgto' : 'pgtos'}
            <ChevronRight className="h-3 w-3 opacity-70" />
          </button>
        ) : (
          <span className="text-[11px] text-muted-foreground tabular-nums whitespace-nowrap">
            {payoutsCount}
          </span>
        )}
      </TableCell>

      {/* 10. Status — padrão "dot + texto" (CommissionStatusBadge) */}
      <TableCell className="px-2 py-1.5">
        <CommissionStatusBadge status={commission.status} />
      </TableCell>

      {/* 11. Tipo de recompensa */}
      <TableCell className="px-2 py-1.5">
        <Badge variant="outline" className="text-[10px] h-5">
          {REWARD_TYPE_LABEL[commission.reward_type] ?? commission.reward_type}
        </Badge>
      </TableCell>

      {/* 12. Valor */}
      <TableCell className="text-right px-2 py-1.5">
        <span className="text-sm font-medium tabular-nums">{valorPrincipal}</span>
      </TableCell>

      {/* 13. Base */}
      <TableCell className="text-right px-2 py-1.5">
        <span className="text-xs text-muted-foreground tabular-nums">
          {commission.base_value ? formatBRL(commission.base_value) : '-'}
        </span>
      </TableCell>

      {/* 14. Vendedor — agora com avatar (mesmo padrão da listagem de Negócios) */}
      <TableCell className="px-2 py-1.5 max-w-[160px]">
        {commission.seller_name ? (
          <div className="flex items-center gap-2 min-w-0">
            <SmartAvatar
              src={commission.seller_avatar_url ?? undefined}
              name={commission.seller_name}
              size="xs"
              defaultFallback="V"
              use3DFallback
            />
            <span className="text-xs text-foreground truncate">
              {commission.seller_name}
            </span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>

      {/* 15. Criada em — data + hora:minuto em duas linhas */}
      <TableCell className="text-xs text-muted-foreground px-2 py-1.5 whitespace-nowrap">
        <div className="leading-tight">
          {format(new Date(commission.created_at), 'dd/MM/yy', { locale: ptBR })}
        </div>
        <div className="text-[10px] text-muted-foreground/70 leading-tight">
          {format(new Date(commission.created_at), 'HH:mm', { locale: ptBR })}
        </div>
      </TableCell>

      {/* 16. Aprovada em — data + hora:minuto em duas linhas */}
      <TableCell className="text-xs text-muted-foreground px-2 py-1.5 whitespace-nowrap">
        {commission.approved_at ? (
          <>
            <div className="leading-tight">
              {format(new Date(commission.approved_at), 'dd/MM/yy', { locale: ptBR })}
            </div>
            <div className="text-[10px] text-muted-foreground/70 leading-tight">
              {format(new Date(commission.approved_at), 'HH:mm', { locale: ptBR })}
            </div>
          </>
        ) : (
          '-'
        )}
      </TableCell>

      {/* 17. Paga em — data + hora:minuto em duas linhas */}
      <TableCell className="text-xs text-muted-foreground px-2 py-1.5 whitespace-nowrap">
        {commission.paid_at ? (
          <>
            <div className="leading-tight">
              {format(new Date(commission.paid_at), 'dd/MM/yy', { locale: ptBR })}
            </div>
            <div className="text-[10px] text-muted-foreground/70 leading-tight">
              {format(new Date(commission.paid_at), 'HH:mm', { locale: ptBR })}
            </div>
          </>
        ) : (
          '-'
        )}
      </TableCell>

      {/* 18. Ações */}
      <TableCell className="px-2 py-1.5 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" className="h-7 w-7">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {/* Ver detalhes — sempre presente quando há handler. */}
            {onOpenDetails && (
              <>
                <DropdownMenuItem onClick={() => onOpenDetails(commission)}>
                  <Eye className="h-4 w-4 mr-2" />
                  Ver detalhes
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {commission.status === 'pending' && (
              <DropdownMenuItem onClick={() => onAction(commission, 'approve')}>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Aprovar
              </DropdownMenuItem>
            )}
            {commission.status === 'approved' && !commission.payout_id && (
              <DropdownMenuItem onClick={() => onAction(commission, 'mark_paid')}>
                <CircleDollarSign className="h-4 w-4 mr-2" />
                Pagar agora
              </DropdownMenuItem>
            )}
            {(commission.status === 'pending' || commission.status === 'approved') && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => onAction(commission, 'cancel')}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancelar
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

interface CommissionsTableViewProps {
  /** Lista de comissões da página atual (server-side). */
  commissions: PaginatedCommission[];
  /** Estado de loading (skeleton). */
  isLoading: boolean;
  /** Callback ao acionar item do menu da linha. */
  onAction: (commission: PaginatedCommission, action: CommissionRowAction) => void;
  /** Mensagem para estado vazio. */
  emptyMessage?: string;
  /** Abre o `CommissionDetailSheet` lateral. */
  onOpenDetails?: (commission: PaginatedCommission) => void;
  /** Abre o modal de drill-down dos pagamentos vinculados à comissão. */
  onOpenPayouts?: (commission: PaginatedCommission) => void;
  /** Abre o modal premium do negócio (CRM). */
  onOpenDeal?: (dealId: string) => void;
  /** Abre o sheet de detalhes da indicação. */
  onOpenReferral?: (referralId: string) => void;
  /** Abre o modal de detalhes do cliente (CRM). */
  onOpenCustomer?: (customerId: string) => void;
}

/**
 * Tabela reutilizável de comissões — paridade visual 99,99% com `CrmDeals`.
 *
 * IMPORTANTE (v2.0.0): este componente NÃO renderiza mais a sub-barra interna
 * (resumo + pills de status). O filtro de status migrou para a barra de
 * filtros principal da página (`ReferralCommissions.tsx`), seguindo o padrão
 * de "Todos Negócios" (`CrmDeals`).
 */
export function CommissionsTableView({
  commissions,
  isLoading,
  onAction,
  emptyMessage = 'Nenhuma comissão encontrada',
  onOpenDetails,
  onOpenPayouts,
  onOpenDeal,
  onOpenReferral,
  onOpenCustomer,
}: CommissionsTableViewProps) {
  // ========== ESTADOS LOCAIS ==========
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // ========== ORDENAÇÃO LOCAL (página atual, igual ao ChargesTableView) ==========
  const sortedCommissions = useMemo(() => {
    return [...commissions].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'affiliate':
          comparison = (a.affiliate_full_name || '').localeCompare(b.affiliate_full_name || '');
          break;
        case 'reward_value':
          comparison = Number(a.reward_value) - Number(b.reward_value);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'approved_at': {
          const da = a.approved_at ? new Date(a.approved_at).getTime() : 0;
          const db = b.approved_at ? new Date(b.approved_at).getTime() : 0;
          comparison = da - db;
          break;
        }
        case 'paid_at': {
          const da = a.paid_at ? new Date(a.paid_at).getTime() : 0;
          const db = b.paid_at ? new Date(b.paid_at).getTime() : 0;
          comparison = da - db;
          break;
        }
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [commissions, sortField, sortDirection]);

  /** Alterna ordenação ao clicar no header. */
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // ========== RESUMO (página atual) ==========
  const totalCommissions = commissions.length;
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    commissions.forEach((c) => {
      counts[c.status] = (counts[c.status] || 0) + 1;
    });
    return counts;
  }, [commissions]);

  // ========== SKELETON LOADING (full-bleed, sem moldura) ==========
  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  // ========== ESTADO VAZIO (full-bleed, sem moldura) ==========
  if (commissions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center">
          <HandCoins className="h-6 w-6 text-muted-foreground" />
        </div>
        <div className="text-center">
          <h3 className="font-medium text-sm">{emptyMessage}</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Tente ajustar os filtros aplicados.
          </p>
        </div>
      </div>
    );
  }

  // ========== MIN-WIDTH DA TABELA ==========
  // v2.2.0: aumentado para acomodar as 6 novas colunas de rastreabilidade
  // (N. Negócio, N. Indicação, Cliente, Vendedor, Programa, Pagamentos).
  const minWidth = '1860px';

  // ========== TABELA FULL-BLEED (sem sub-barra, sem moldura externa) ==========
  // Espelha o padrão de `CrmDeals`: sem `rounded-lg border` externo, tabela
  // colada nas bordas, scroll horizontal interno via `overflow-auto`.
  return (
    <ScrollIndicators className="flex flex-col h-full min-h-0 min-w-0 w-full max-w-full">
      <div ref={scrollContainerRef} data-scroll-viewport className="flex-1 min-h-0 w-full max-w-full overflow-auto">
        <table
          className="w-max caption-bottom text-sm table-fixed"
          style={{ minWidth }}
        >
            {/* =====================================================
                Larguras de colunas (v2.3.0 — 18 colunas)
                Nova ordem solicitada:
                  Afiliado movido para pos. 4 (depois de N. Negócio)
                  Vendedor movido para pos. 14 (depois de Base) com avatar
                IMPORTANTE: a ordem aqui DEVE bater 1:1 com o <TableHead>
                e com as <TableCell> em CommissionTableRow.
                ===================================================== */}
            <colgroup>
              <col style={{ width: 8 }} />   {/* 1. Barra de status */}
              <col className="w-[80px]" />   {/* 2. Ref. */}
              <col className="w-[90px]" />   {/* 3. N. Negócio */}
              <col className="w-[170px]" />  {/* 4. Afiliado */}
              <col className="w-[90px]" />   {/* 5. N. Indicação */}
              <col className="w-[170px]" />  {/* 6. Cliente */}
              <col className="w-[130px]" />  {/* 7. Programa */}
              <col className="w-[130px]" />  {/* 8. Campanha */}
              <col className="w-[100px]" />  {/* 9. Pagamentos */}
              <col className="w-[95px]" />   {/* 10. Status */}
              <col className="w-[90px]" />   {/* 11. Tipo */}
              <col className="w-[110px]" />  {/* 12. Valor */}
              <col className="w-[100px]" />  {/* 13. Base */}
              <col className="w-[160px]" />  {/* 14. Vendedor (com avatar) */}
              <col className="w-[80px]" />   {/* 15. Criada */}
              <col className="w-[80px]" />   {/* 16. Aprovada */}
              <col className="w-[80px]" />   {/* 17. Paga */}
              <col className="w-[60px]" />   {/* 18. Ações */}
            </colgroup>

            {/* Header sticky — 18 colunas alinhadas ao colgroup e às células */}
            <TableHeader className="sticky top-0 bg-background z-[1]">
              <TableRow className="bg-muted/20 hover:bg-muted/20 border-b border-border/40">
                {/* 1. Barra de status */}
                <TableHead className="w-2 p-0" />
                {/* 2. Ref. */}
                <TableHead className="text-xs font-medium px-2 py-2">Ref.</TableHead>
                {/* 3. N. Negócio */}
                <TableHead className="text-xs font-medium px-2 py-1.5">N. Negócio</TableHead>
                {/* 4. Afiliado (ordenável) — movido para depois de N. Negócio */}
                <SortableHeader
                  field="affiliate"
                  label="Afiliado"
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                {/* 5. N. Indicação */}
                <TableHead className="text-xs font-medium px-2 py-1.5">N. Indicação</TableHead>
                {/* 6. Cliente */}
                <TableHead className="text-xs font-medium px-2 py-1.5">Cliente</TableHead>
                {/* 7. Programa */}
                <TableHead className="text-xs font-medium px-2 py-1.5">Programa</TableHead>
                {/* 8. Campanha */}
                <TableHead className="text-xs font-medium px-2 py-1.5">Campanha</TableHead>
                {/* 9. Pagamentos */}
                <TableHead className="text-xs font-medium px-2 py-1.5">Pagamentos</TableHead>
                {/* 10. Status (ordenável) */}
                <SortableHeader
                  field="status"
                  label="Status"
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                {/* 11. Tipo */}
                <TableHead className="text-xs font-medium px-2 py-1.5">Tipo</TableHead>
                {/* 12. Valor (ordenável, alinhado à direita) */}
                <SortableHeader
                  field="reward_value"
                  label="Valor"
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                {/* 13. Base */}
                <TableHead className="text-xs font-medium text-right px-2 py-1.5">
                  Base
                </TableHead>
                {/* 14. Vendedor — movido para depois de Base */}
                <TableHead className="text-xs font-medium px-2 py-1.5">Vendedor</TableHead>
                {/* 15. Criada (ordenável) */}
                <SortableHeader
                  field="created_at"
                  label="Criada"
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                {/* 16. Aprovada (ordenável) */}
                <SortableHeader
                  field="approved_at"
                  label="Aprovada"
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                {/* 17. Paga (ordenável) */}
                <SortableHeader
                  field="paid_at"
                  label="Paga"
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                {/* 18. Ações */}
                <TableHead className="text-xs font-medium text-right px-2 py-1.5">
                  Ações
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {sortedCommissions.map((c) => (
                <CommissionTableRow
                  key={c.id}
                  commission={c}
                  onAction={onAction}
                  // v2.2.0 — propaga callbacks de drill-down até a linha
                  onOpenDetails={onOpenDetails}
                  onOpenPayouts={onOpenPayouts}
                  onOpenDeal={onOpenDeal}
                  onOpenReferral={onOpenReferral}
                  onOpenCustomer={onOpenCustomer}
                />
              ))}
          </TableBody>
        </table>
      </div>
    </ScrollIndicators>
  );
}

